import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { Table, TablePagination } from "components/base";
import { ROUTES } from "utils/constants";
import { useHistory } from "react-router-dom";
import {
  UpdateVehicle,
  functionalStatusCell,
  renderAssignees,
  renderUtilizationStatus,
  functionalStatusOnOffCell,
  serviceStatusCell,
} from "./cell-renders";
import { parseVehiclesTable } from "utils/parsers";
import {
  getClients,
  initializeVehicles,
  setBulkActionsVehicles,
  setDynamicFilters,
  setClientDynamicFilters,
  setModels,
  setSelectedVehicles,
  setVehiclesSelectedPage,
  setSelectedVehicle,
} from "store/actions";
import { TableProps } from "antd/lib/table";
import useDebounce, { useInterval } from "utils/hooks";
import { POLLING_MSECS } from "utils/constants";
import { Vehicle } from "../../../types";
import { Key } from "react";
import { WarningOutlined, PoweroffOutlined} from "@ant-design/icons";
import { speedLimitIcon } from "svgs";

import { Button, Checkbox, Dropdown, Menu, Card, Tag, Avatar } from "antd";
import {
  DownOutlined,
  CarOutlined,
  UserOutlined,
  SettingOutlined,
  RightOutlined,
} from "@ant-design/icons";

const STORAGE_KEY = "bike_ops_visible_columns";

const getColumns: (userType: number) => TableProps<any>["columns"] = (
  userType
) => {
  const clients = {
    title: "Client",
    dataIndex: "partner",
    width: 160,
    ellipsis: false,
  };
  const columns: TableProps<any>["columns"] = [
{
  title: "Vehicle ID",
  dataIndex: "vehicle_id",
  width: 120,
  ellipsis: true,
render: (text: string, record: any) => {
  const limpOn = record.raw?.isLimpModeOn;
  const remoteOff = record.raw?.isRemoteLock;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span>{text}</span>
      {limpOn && (
       <span
  title="Limp Mode Enabled"
  style={{ width: 16, height: 16, color: "#f8b400" }}
>
  {speedLimitIcon}
</span>
      )}
      {remoteOff && (
        <PoweroffOutlined
          title="Remote Turnoff Enabled"
          style={{ color: "#ff4d4f" }}
        />
      )}
    </div>
  );
},
},
    {
      title: "Billing",
      dataIndex: "is_billing_active",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Model",
      dataIndex: "model",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Functional Status",
      dataIndex: "vehicle_functional_status",
      render: functionalStatusOnOffCell,
      width: 150,
      ellipsis: true,
    },
    {
      title: "Assignee",
      dataIndex: "driver_name",
      width: 100,
      ellipsis: true,
      render: renderAssignees,
    },
    {
      title: "Device ID",
      dataIndex: "iot_id",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Service Status",
      dataIndex: "vehicle_service_status",
      width: 140,
      ellipsis: true,
      render: serviceStatusCell,
    },
    {
      title: "Zone",
      dataIndex: "zone",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Battery",
      dataIndex: "battery",
      width: 90,
      ellipsis: true,
    },
    {
      title: "Checked Out",
      dataIndex: "checkout_by_name",
      width: 130,
      ellipsis: true,
    },
    {
      title: "VIN",
      dataIndex: "vin",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Network ID",
      dataIndex: "network_id",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Color",
      dataIndex: "color",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Firmware Version",
      dataIndex: "f_version",
      width: 130,
      ellipsis: true,
    },
    {
      title: "",
      dataIndex: "action",
      render: UpdateVehicle,
      align: "left",
      width: 56,
      className: "action-menu open-link --user",
    },
  ];
  const colWithClients = [...columns];
  colWithClients.splice(1, 0, clients);

  if (userType === 1 || userType === 2) {
    return colWithClients;
  }
  if (userType === 6) {
    return columns.filter(
      (column) =>
        column.title !== "Device ID" &&
        column.title !== "Billing" &&
        column.title !== "Network ID" &&
        column.title !== "Checked Out"
    );
  }
  if (userType === 8) {
    return columns.filter(
      (column) => column.title !== "Assignee" && column.title !== "Billing"
    );
  }

  return columns.filter(
    (column) =>
      column.title !== "Device ID" &&
      column.title !== "VIN" &&
      column.title !== "Billing" &&
      column.title !== "Network ID" &&
      column.title !== "Firmware Version"
  );
};

// Mobile Card Component
interface VehicleCardProps {
  vehicle: any;
  onClick: () => void;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
}

const VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  onClick,
  isSelected,
  onSelect,
}) => {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "on":
        return "green";
      case "inactive":
      case "off":
        return "red";
      case "maintenance":
        return "orange";
      default:
        return "default";
    }
  };

  const limpOn = vehicle.raw?.isLimpModeOn;
  const remoteOff = vehicle.raw?.isRemoteLock;

  return (
    <Card
      className={`vehicle-mobile-card ${isSelected ? "selected" : ""}`}
      onClick={onClick}
    >
      <div className="card-header">
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          className="card-checkbox"
        />
       
        <div className="vehicle-main-info">
          <div className="vehicle-id-wrapper">
            <h4 className="vehicle-id">{vehicle.vehicle_id || "N/A"}</h4>
            <div className="vehicle-status-icons">
              {limpOn && (
                <span
                  title="Limp Mode Enabled"
                  className="status-icon limp-mode-icon"
                >
                  {speedLimitIcon}
                </span>
              )}
              {remoteOff && (
                <PoweroffOutlined
                  title="Remote Turnoff Enabled"
                  className="status-icon remote-off-icon"
                />
              )}
            </div>
          </div>
          <span className="vehicle-model">{vehicle.model || "N/A"}</span>
        </div>
        {/* Action Button */}
        <div 
          className="card-action-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          {UpdateVehicle(vehicle.action, vehicle)}
        </div>
      </div>

      <div className="card-body">
        <div className="info-row">
          <span className="info-label">Functional Status</span>
          <Tag color={getStatusColor(vehicle.vehicle_functional_status)}>
            {vehicle.vehicle_functional_status || "N/A"}
          </Tag>
        </div>

        <div className="info-row">
          <span className="info-label">Service Status</span>
          <Tag color={getStatusColor(vehicle.vehicle_service_status)}>
            {vehicle.vehicle_service_status || "N/A"}
          </Tag>
        </div>

        <div className="info-row">
          <span className="info-label">Assignee</span>
          <span className="info-value">
            {vehicle.driver_name || "Unassigned"}
          </span>
        </div>

        <div className="info-row">
          <span className="info-label">Battery</span>
          <span className="info-value">{vehicle.battery || "N/A"}</span>
        </div>

        {vehicle.zone && (
          <div className="info-row">
            <span className="info-label">Zone</span>
            <span className="info-value">{vehicle.zone}</span>
          </div>
        )}

        {vehicle.partner && (
          <div className="info-row">
            <span className="info-label">Client</span>
            <span className="info-value">{vehicle.partner}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default function TableView() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  const {
    vehicles: {
      vehicles,
      page,
      search,
      models,
      clients,
      filters: { updated },
    },
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);
  const { selectedPage, totalPages } = page;

  const allColumns = useMemo(() => getColumns(userType), [userType]);

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    if (userType === 2) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return (allColumns ?? []).map(
            (col) => (col as any).dataIndex || (col as any).key
          );
        }
      }
    }
    return (allColumns ?? []).map(
      (col) => (col as any).dataIndex || (col as any).key
    );
  });

  const data = useMemo(() => parseVehiclesTable(vehicles), [vehicles]);

  //  Track selected keys across ALL pages
  const [allSelectedKeys, setAllSelectedKeys] = useState<Set<Key>>(
    new Set()
  );

  //  Cache to store raw vehicle data from ALL pages
  const [selectedItemsCache, setSelectedItemsCache] = useState<
    Map<Key, any>
  >(() => new Map());

  //  When current page data loads, update cache for already-selected items
  useEffect(() => {
    if (!data) return;
    setSelectedItemsCache((prevCache) => {
      const newCache = new Map(prevCache);
      data.forEach((item: any) => {
        if (allSelectedKeys.has(item.key)) {
          newCache.set(item.key, item.raw || item);
        }
      });
      return newCache;
    });
  }, [data, allSelectedKeys]);

  //  Derived: selected keys that exist on the CURRENT page only
  const currentPageSelectedKeys = useMemo(() => {
    if (!data) return [];
    return data
      .filter((item: any) => allSelectedKeys.has(item.key))
      .map((item: any) => item.key);
  }, [data, allSelectedKeys]);

  // ─── Derived counts ───
  const totalCount = data?.length ?? 0;
  const selectedCount = allSelectedKeys.size;
  const currentPageSelectedCount = currentPageSelectedKeys.length;

  const limpModeCount = useMemo(
  () => data?.filter((v: any) => v.raw?.isLimpModeOn)?.length || 0,
  [data]
);

const remoteTurnoffCount = useMemo(
  () => data?.filter((v: any) => v.raw?.isRemoteLock)?.length || 0,
  [data]
);

  // Handle responsive view change
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onBack = useCallback(() => {
    if (selectedPage > 1) {
      dispatch(setVehiclesSelectedPage(selectedPage - 1));
    }
  }, [dispatch, selectedPage]);

  const onNext = useCallback(() => {
    if (selectedPage < totalPages) {
      dispatch(setVehiclesSelectedPage(selectedPage + 1));
    }
  }, [dispatch, selectedPage, totalPages]);

  const resetCallback = useCallback(() => {
    dispatch(setVehiclesSelectedPage(1));
  }, [dispatch]);

  const debounceSearchString = useDebounce(search, 1000);

  useEffect(() => {
    if (userType === 2) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleKeys));
    }
  }, [visibleKeys, userType]);

  useEffect(() => {
    dispatch(setModels());
    dispatch(getClients());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setDynamicFilters(models));
    dispatch(setClientDynamicFilters(clients));
  }, [models, clients, dispatch]);

  useInterval(() => {
    dispatch(initializeVehicles(selectedPage, updated, false, false));
  }, POLLING_MSECS);

  useEffect(() => {
    dispatch(initializeVehicles(selectedPage, updated));
  }, [dispatch, selectedPage, updated, debounceSearchString]);

  // Sync Redux using the cache — includes ALL pages
  const syncReduxSelection = useCallback(
    (newKeys: Set<Key>, updatedCache: Map<Key, any>) => {
      if (newKeys.size > 0) {
        dispatch(setBulkActionsVehicles(true));

        // Pull from cache for ALL selected keys (not just current page)
        const selectedItems: (Vehicle | null)[] = [];
        newKeys.forEach((key) => {
          const cached = updatedCache.get(key);
          if (cached) {
            selectedItems.push(cached);
          }
        });

        dispatch(setSelectedVehicles(selectedItems));
      } else {
        dispatch(setBulkActionsVehicles(false));
        dispatch(setSelectedVehicles([]));
      }
    },
    [dispatch]
  );

  // Handle table selection change — merge with cross-page selections
  const onChangeSelection = useCallback(
    (newPageKeys: (string | number)[]) => {
      const newAllKeys = new Set(allSelectedKeys);
      const newCache = new Map(selectedItemsCache);

      // Get all keys on the current page
      const currentPageKeys = new Set(
        (data ?? []).map((item: any) => item.key)
      );

      // Remove all current page keys first
      currentPageKeys.forEach((key) => {
        newAllKeys.delete(key);
        newCache.delete(key);
      });

      // Add back only the ones now selected on this page
      newPageKeys.forEach((key) => {
        newAllKeys.add(key);
        const item = data?.find((d: any) => d.key === key);
        if (item) {
          newCache.set(key, item.raw || item);
        }
      });

      setAllSelectedKeys(newAllKeys);
      setSelectedItemsCache(newCache);
      syncReduxSelection(newAllKeys, newCache);
    },
    [data, allSelectedKeys, selectedItemsCache, syncReduxSelection]
  );

  // Clear ALL selections across all pages
  const clearAllSelections = useCallback(() => {
    setAllSelectedKeys(new Set());
    setSelectedItemsCache(new Map());
    dispatch(setBulkActionsVehicles(false));
    dispatch(setSelectedVehicles([]));
  }, [dispatch]);

  const handleRowClick = (vehicle: any) => {
    dispatch(setSelectedVehicle(vehicle));
    history.push(`${ROUTES.vehicle_detail}/${vehicle?.id}`);
  };

  // ✅ Mobile card selection using unique keys + cache
  const handleMobileCardSelect = useCallback(
    (vehicleKey: Key, checked: boolean) => {
      const newAllKeys = new Set(allSelectedKeys);
      const newCache = new Map(selectedItemsCache);

      if (checked) {
        newAllKeys.add(vehicleKey);
        const item = data?.find((d: any) => d.key === vehicleKey);
        if (item) {
          newCache.set(vehicleKey, item.raw || item);
        }
      } else {
        newAllKeys.delete(vehicleKey);
        newCache.delete(vehicleKey);
      }

      setAllSelectedKeys(newAllKeys);
      setSelectedItemsCache(newCache);
      syncReduxSelection(newAllKeys, newCache);
    },
    [allSelectedKeys, selectedItemsCache, data, syncReduxSelection]
  );

  const filteredColumns = (allColumns ?? []).filter((col) => {
    const key =
      "children" in col ? col.key : (col as any).dataIndex || col.key;
    if ("dataIndex" in col && col.dataIndex === "action") return true;
    return visibleKeys.includes(key);
  });

  const columnMenu =
    userType === 2 ? (
      <Menu selectable={false} className="column-select-menu">
        {(allColumns ?? [])
          .filter((col) =>
            "dataIndex" in col ? col.dataIndex !== "action" : true
          )
          .map((col) => {
            const key =
              "children" in col
                ? col.key
                : (col as any).dataIndex || col.key;
            return (
              <Menu.Item
                key={key}
                onClick={(e) => e.domEvent.stopPropagation()}
                className="column-menu-item"
              >
                <div className="column-checkbox-wrapper">
                  <Checkbox
                    checked={visibleKeys.includes(key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setVisibleKeys([...visibleKeys, key]);
                      } else {
                        setVisibleKeys(
                          visibleKeys.filter((k) => k !== key)
                        );
                      }
                    }}
                  >
                    {col.title || "Column"}
                  </Checkbox>
                </div>
              </Menu.Item>
            );
          })}
      </Menu>
    ) : null;

  return (
    <div className="vehicles-table-container">
      {/* Column Selector */}
      {userType === 2 && columnMenu && (
        <div className="column-selector-wrapper">
          <Dropdown
            overlay={columnMenu}
            trigger={["click"]}
            visible={dropdownOpen}
            onVisibleChange={(visible: boolean) => setDropdownOpen(visible)}
            placement="bottomRight"
          >
            <Button className="column-select-btn">
              <span className="btn-content">
                <SettingOutlined className="btn-icon" />
                <span className="btn-text">Columns</span>
                <DownOutlined className="btn-arrow" />
              </span>
            </Button>
          </Dropdown>
        </div>
      )}

      {/* Desktop/Tablet Table View */}
      <div className="desktop-table-view">
        <div className="table-scroll-wrapper">
          <Table
            columns={filteredColumns}
            dataSource={data}
            onRow={(record) => ({
              onClick: () => handleRowClick(record.raw),
            })}
            rowSelection={{
              // ✅ Only current-page keys passed to table
              selectedRowKeys: currentPageSelectedKeys,
              onChange: onChangeSelection,
            }}
            pagination={false}
          //  scroll={{ x: "max-content" }}
            className="vehicles-table"
          />
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-card-view">
        {selectedCount > 0 && (
          <div className="mobile-selection-bar">
            <span>
              {selectedCount} selected
              {currentPageSelectedCount !== selectedCount && (
                <> ({currentPageSelectedCount} on this page)</>
              )}
            </span>
            <Button
              type="link"
              onClick={clearAllSelections}
              className="clear-selection-btn"
            >
              Clear All
            </Button>
          </div>
        )}

        <div className="mobile-cards-list">
          {data?.map((vehicle: any, index: number) => (
            <VehicleCard
              key={vehicle.key || index}
              vehicle={vehicle}
              onClick={() => handleRowClick(vehicle.raw)}
              // ✅ Use unique key instead of index
              isSelected={allSelectedKeys.has(vehicle.key)}
              onSelect={(checked) =>
                handleMobileCardSelect(vehicle.key, checked)
              }
            />
          ))}
        </div>

        {(!data || data.length === 0) && (
          <div className="no-data-message">
            <CarOutlined className="no-data-icon" />
            <p>No vehicles found</p>
          </div>
        )}
      </div>

      {/* ─── Bottom bar: selection indicator + pagination ─── */}
      <div className="bottom-bar-wrapper">
             {(limpModeCount > 0 || remoteTurnoffCount > 0) && (
  <div className="mode-indicators">
    {limpModeCount > 0 && (
      <span className="mode-indicator limp-mode">
         <span className="mode-icon-wrapper">{speedLimitIcon}</span>{" "}
        Limp Mode Enabled
        {/* Limp Mode Enabled : <strong>{limpModeCount}</strong> */}
      </span>
    )}
    {remoteTurnoffCount > 0 && (
      <span className="mode-indicator remote-turnoff">
        <PoweroffOutlined style={{ color: "#ff4d4f" }} />{" "}
        Remote Turnoff Enabled
        {/* Remote Turnoff Enabled: <strong>{remoteTurnoffCount}</strong> */}
      </span>
    )}
  </div>
)}
        {selectedCount > 0 && (
          <div className="selection-indicator">
            <span className="selection-indicator-text">
              Selected <strong>{selectedCount}</strong> total
              {currentPageSelectedCount > 0 && selectedCount > currentPageSelectedCount &&(
                <> ({currentPageSelectedCount} on this page)</>
              )}
            </span>
            <Button
              type="link"
              size="small"
              onClick={clearAllSelections}
              className="selection-clear-btn"
            >
              Clear all selections
            </Button>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination-wrapper">
          <TablePagination
            page={page}
            onBack={onBack}
            onNext={onNext}
            resetCallback={resetCallback}
          />
        </div>
      </div>
    </div>
  );
}